import { commaListSchema } from "@birdwatch/common";
import { z } from "zod";

import {
  filterSearchFragment,
  notificationSearchFragment,
  withOperators,
} from "@/features/shared/filters-new";
import { maskSearchFragment } from "@/features/shared/route-masking";

import { smartActionShadowSearchFragment } from "../shadow/smart-action-shadow-search";

/**
 * The Value filter's URL shape, `{ min, max }` in whole dollars (the
 * `priceRange` widget's). The same shape as the local `priceRangeSchema` in
 * `routes/office/billing.invoices.index.tsx` — a second copy under the rule of
 * three — plus a `.catch`, so a hand-edited link cannot take the page down.
 */
const valueRangeSchema = z
  .object({
    min: z.coerce.number().optional(),
    max: z.coerce.number().optional(),
  })
  .optional()
  .or(z.literal(""))
  .catch(undefined);

/**
 * `/office/smart-actions/board`'s search. Lenient throughout: a strict enum on a
 * URL param throws `SearchParamError` and crashes the page
 * (`.claude/rules/webapp-routing.md`), so the board, the bands and the due
 * choice are plain strings narrowed in the feature.
 */
export const smartActionBoardSearchSchema = z.object({
  /** sales | operations | care — narrowed by `toSmartActionBoard`; absent means Sales. */
  board: z.string().optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  ...filterSearchFragment,
  filters: z
    .object({
      ownerUserIds: withOperators(commaListSchema),
      managerUserIds: withOperators(commaListSchema),
      type: withOperators(commaListSchema),
      program: withOperators(commaListSchema),
      status: withOperators(commaListSchema),
      costStatus: withOperators(commaListSchema),
      flag: withOperators(z.string().optional()),
      // Dollars in the URL; the field's `toApiFilter` sends cents as gte/lte.
      valueCents: valueRangeSchema,
      // A band key (good | fair | low); `toApiFilter` maps it to gte/lte.
      chancePct: z.string().optional().catch(undefined),
      // overdue | today | week; `toApiFilter` maps it to lte.
      dueAt: z.string().optional().catch(undefined),
      // The notification inbox in the office sidebar writes these on any route.
      ...notificationSearchFragment,
    })
    .optional(),
  sort: z.string().optional().catch(undefined),
  /** `only` shows the actions on "[QA]" test orders; read on beta alone (`_libs/test-orders.ts`). */
  testOrders: z.string().optional().catch(undefined),
  /** The open popup (`?smartActionId=`, a shadow route) — deep-linkable, so it fetches its own record. */
  ...smartActionShadowSearchFragment,
  ...maskSearchFragment,
});
